import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { Box, Paper, Stack } from '@mui/material';
import React from 'react';
import AIChatMessage from '../../models/AIChatMessage';
import AddressMapResult, { CustomMapBounds } from '../../models/AddressMapResult';
import MultiMap from '../MultiMap';
import { Palette } from 'Palette';
import { StyledHeading, StyledTypography } from '../styles/styles';
import { FOOTER_POINTS } from './constants';
import FundOpportunityChatInput from './FundOpportunityChatInput';
import FundOpportunityChatMessages from './FundOpportunityChatMessages';

export interface FundOpportunityChatInterfaceProps {
  messages: AIChatMessage[];
  isProcessing: boolean;
  addresses: AddressMapResult[];
  mapBounds?: CustomMapBounds;
  showMap: boolean;
  allowedForImpersonation: boolean;
  hasAcceptedDisclaimer: boolean;
  inputValue: string;
  hasMessages: boolean;
  lastMessageSender: AIChatMessage['role'] | null;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  onInputChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onPresetClick: (prompt: string) => void;
}

const FundOpportunityChatInterface: React.FC<FundOpportunityChatInterfaceProps> = ({
  messages,
  isProcessing,
  addresses,
  mapBounds,
  showMap,
  allowedForImpersonation,
  hasAcceptedDisclaimer,
  inputValue,
  hasMessages,
  lastMessageSender,
  chatContainerRef,
  onInputChange,
  onSubmit,
  onPresetClick,
}) => (
  <Box
    sx={{
      backgroundColor: '#ffffff',
      borderRadius: '32px',
      px: { xs: 3, md: 6 },
      py: { xs: 4, md: 6 },
      maxWidth: '960px',
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 3, md: 4 },
      minHeight: { xs: '560px', md: '640px' },
    }}
  >
    {allowedForImpersonation && !hasAcceptedDisclaimer && (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          px: { xs: 2.5, md: 3 },
          py: 2,
          borderRadius: '18px',
          backgroundColor: '#fff5f5',
          border: '1px solid rgba(220, 38, 38, 0.2)',
        }}
      >
        <StyledTypography
          sx={{
            fontSize: '0.95rem',
            color: '#7f1d1d',
          }}
        >
          Note: This user has not accepted the Vera AI disclaimer.
        </StyledTypography>
      </Paper>
    )}

    <Stack spacing={1} alignItems="flex-start">
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoAwesome fontSize="large" sx={{ color: Palette.default.buttonBg }} />
        <StyledHeading headerVariant="subheading" noTopPadding sx={{ fontSize: '1.75rem', mb: 0 }}>
          Vera AI
        </StyledHeading>
      </Stack>
    </Stack>

    <Box
      sx={{
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {hasMessages ? (
        <>
          <FundOpportunityChatMessages
            messages={messages}
            isProcessing={isProcessing}
            lastMessageSender={lastMessageSender}
            chatContainerRef={chatContainerRef}
          />
          {showMap && (
            <Box
              sx={{
                borderRadius: '24px',
                overflow: 'hidden',
                border: `1px solid ${Palette.tone1.border}`,
              }}
            >
              <MultiMap addresses={addresses} mapBounds={mapBounds} height="320px" />
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 3, md: 4 },
            py: { xs: 6, md: 8 },
          }}
        >
          <StyledHeading
            headerVariant="subheading"
            noTopPadding
            sx={{
              fontSize: { xs: '1.4rem', md: '1.6rem' },
              fontWeight: 600,
              color: Palette.tone1.text,
              textAlign: 'center',
              maxWidth: '520px',
              mt: 0,
            }}
          >
            What would you like to know more about?
          </StyledHeading>

          <FundOpportunityChatInput
            variant="initial"
            inputValue={inputValue}
            isProcessing={isProcessing}
            onInputChange={onInputChange}
            onSubmit={onSubmit}
            onPresetClick={onPresetClick}
          />
        </Box>
      )}
    </Box>

    {hasMessages && (
      <FundOpportunityChatInput
        variant="chat"
        inputValue={inputValue}
        isProcessing={isProcessing}
        onInputChange={onInputChange}
        onSubmit={onSubmit}
        onPresetClick={onPresetClick}
      />
    )}

    <Box
      sx={{
        textAlign: 'center',
        mt: hasMessages ? 2 : 4,
      }}
    >
      <StyledTypography
        sx={{
          fontSize: '0.85rem',
          color: '#000000',
          mb: 1,
        }}
      >
        Vera is an experimental assistant to help you explore investment opportunities. Please note:
      </StyledTypography>
      <Box
        component="ul"
        sx={{
          listStyle: 'disc',
          px: { xs: 2, md: 6 },
          color: '#000000',
          m: 0,
          textAlign: 'left',
          maxWidth: '720px',
          mx: 'auto',
        }}
      >
        {FOOTER_POINTS.map((point) => (
          <StyledTypography
            key={point}
            component="li"
            sx={{
              fontSize: '0.8rem',
              mb: 0.75,
              color: '#000000',
            }}
          >
            {point}
          </StyledTypography>
        ))}
      </Box>
    </Box>
  </Box>
);

export default FundOpportunityChatInterface;
